import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { MapPin, Wallet, Clock, Loader2, AlertCircle } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useAccessibleLocations } from '../hooks/useAccessibleLocations';
import { reportsService } from '../services/reportsService';
import { toast } from 'sonner';
import { REPORT_SHIFTS } from '../constants/reportFilters';
import { DateRangeQuickFilter } from '@/components/ui/date-range-quick-filter';
import { useDateRangeDefaults } from '@/hooks/useDateRangeDefaults';

interface CashRegisterFilters {
  locationId: string;
  shift: string;
  dateFrom: string;
  dateTo: string;
}


interface CashRegisterTransaction {
  id: string;
  time: string;
  date?: string;
  type: 'ingreso' | 'egreso';
  description: string;
  amount: number;
  shift: string;
  shiftName: string;
  sessionId?: string;
}

interface CashRegisterReportState {
  loading: boolean;
  error: string | null;
  transactions: CashRegisterTransaction[];
  cashCount: {
    bills: Record<string, number>;
    coins: Record<string, number>;
    totalCounted: number;
  } | null;
  summary: {
    openingAmount: number;
    totalSales: number;
    totalExpenses: number;
    netMovement: number;
    expectedBalance: number;
  };
  isOpened: boolean;
}


const mockTransactions = [
  // Turno Mañana (6:00 - 14:00)
  { id: 1, time: '09:15', type: 'ingreso', description: 'Venta #001', amount: 25.50, shift: 'morning', shiftName: 'Mañana' },
  { id: 2, time: '09:32', type: 'ingreso', description: 'Venta #002', amount: 18.75, shift: 'morning', shiftName: 'Mañana' },
  { id: 3, time: '10:05', type: 'egreso', description: 'Compra ingredientes', amount: -45.00, shift: 'morning', shiftName: 'Mañana' },
  { id: 4, time: '10:28', type: 'ingreso', description: 'Venta #003', amount: 32.25, shift: 'morning', shiftName: 'Mañana' },
  { id: 5, time: '11:15', type: 'ingreso', description: 'Venta #004', amount: 28.50, shift: 'morning', shiftName: 'Mañana' },
  { id: 6, time: '11:45', type: 'egreso', description: 'Servicios básicos', amount: -120.00, shift: 'morning', shiftName: 'Mañana' },
  { id: 7, time: '12:20', type: 'ingreso', description: 'Venta #005', amount: 45.75, shift: 'morning', shiftName: 'Mañana' },
  { id: 8, time: '13:10', type: 'ingreso', description: 'Venta #006', amount: 22.00, shift: 'morning', shiftName: 'Mañana' },
  
  // Turno Tarde (14:00 - 22:00)
  { id: 9, time: '14:30', type: 'ingreso', description: 'Venta #007', amount: 35.25, shift: 'afternoon', shiftName: 'Tarde' },
  { id: 10, time: '15:15', type: 'ingreso', description: 'Venta #008', amount: 42.50, shift: 'afternoon', shiftName: 'Tarde' },
  { id: 11, time: '16:00', type: 'egreso', description: 'Mantenimiento equipo', amount: -80.00, shift: 'afternoon', shiftName: 'Tarde' },
  { id: 12, time: '16:45', type: 'ingreso', description: 'Venta #009', amount: 28.75, shift: 'afternoon', shiftName: 'Tarde' },
  { id: 13, time: '17:30', type: 'ingreso', description: 'Venta #010', amount: 52.00, shift: 'afternoon', shiftName: 'Tarde' },
  { id: 14, time: '18:15', type: 'ingreso', description: 'Venta #011', amount: 31.25, shift: 'afternoon', shiftName: 'Tarde' },
  { id: 15, time: '19:00', type: 'egreso', description: 'Compra suministros', amount: -65.00, shift: 'afternoon', shiftName: 'Tarde' },
  { id: 16, time: '20:30', type: 'ingreso', description: 'Venta #012', amount: 38.50, shift: 'afternoon', shiftName: 'Tarde' },
  
  // Turno Noche (22:00 - 6:00)
  { id: 17, time: '22:15', type: 'ingreso', description: 'Venta #013', amount: 24.75, shift: 'night', shiftName: 'Noche' },
  { id: 18, time: '23:30', type: 'ingreso', description: 'Venta #014', amount: 19.50, shift: 'night', shiftName: 'Noche' },
  { id: 19, time: '00:45', type: 'egreso', description: 'Limpieza profunda', amount: -40.00, shift: 'night', shiftName: 'Noche' },
  { id: 20, time: '01:20', type: 'ingreso', description: 'Venta #015', amount: 15.25, shift: 'night', shiftName: 'Noche' },
  { id: 21, time: '03:00', type: 'ingreso', description: 'Venta #016', amount: 22.00, shift: 'night', shiftName: 'Noche' },
];

const mockCashCount = [
  // Billetes
  { denomination: 200, type: 'billete', quantity: 5, total: 1000 },
  { denomination: 100, type: 'billete', quantity: 8, total: 800 },
  { denomination: 50, type: 'billete', quantity: 12, total: 600 },
  { denomination: 20, type: 'billete', quantity: 15, total: 300 },
  { denomination: 10, type: 'billete', quantity: 20, total: 200 },
  
  // Monedas
  { denomination: 5, type: 'moneda', quantity: 25, total: 125 },
  { denomination: 2, type: 'moneda', quantity: 30, total: 60 },
  { denomination: 1, type: 'moneda', quantity: 40, total: 40 },
  { denomination: 0.50, type: 'moneda', quantity: 35, total: 17.50 },
  { denomination: 0.20, type: 'moneda', quantity: 45, total: 9.00 },
  { denomination: 0.10, type: 'moneda', quantity: 50, total: 5.00 },
  { denomination: 0.05, type: 'moneda', quantity: 30, total: 1.50 },
];


export const CashRegisterReport: React.FC = () => {
  const { user } = useAuth();
  const { locationsWithAll, defaultLocationId, loading: locationsLoading } = useAccessibleLocations();
  const { quickRange, dateFrom, dateTo, setQuickRange, setManualDates } = useDateRangeDefaults('cashRegisterReport');

  const [filters, setFilters] = useState<CashRegisterFilters>({
    locationId: user?.locationId || 'all',
    shift: 'all',
    dateFrom: '',
    dateTo: ''
  });

  const [reportData, setReportData] = useState<CashRegisterReportState>({
    loading: false,
    error: null,
    transactions: [],
    cashCount: null,
    summary: {
      openingAmount: 0,
      totalSales: 0,
      totalExpenses: 0,
      netMovement: 0,
      expectedBalance: 0,
    },
    isOpened: false,
  });

  // Auto-seleccionar local cuando se carga
  useEffect(() => {
    if (defaultLocationId) {
      setFilters(prev => ({ ...prev, locationId: defaultLocationId }));
    }
  }, [defaultLocationId]);

  const updateFilter = (key: keyof CashRegisterFilters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Función para agrupar transacciones de ventas por método de pago
  const groupTransactionsByPaymentMethod = (transactions: any[]) => {
    const salesTransactions = transactions.filter(t => 
      t.type === 'ingreso' && 
      t.description.includes('Venta') &&
      !String(t.id).includes('opening-balance') &&
      !String(t.id).includes('closing-balance') &&
      !String(t.id).includes('difference-')
    );
    
    const otherTransactions = transactions.filter(t => 
      !(t.type === 'ingreso' && t.description.includes('Venta')) ||
      String(t.id).includes('opening-balance') ||
      String(t.id).includes('closing-balance') ||
      String(t.id).includes('difference-')
    );
    
    // Agrupar ventas por método de pago
    const paymentGroups: { [key: string]: any } = {};
    
    salesTransactions.forEach(transaction => {
      // Extraer método de pago de la descripción
      let paymentMethod = 'Efectivo'; // default
      if (transaction.description.includes('(Tarjeta)')) {
        paymentMethod = 'Tarjeta';
      } else if (transaction.description.includes('(Yape)') || transaction.description.includes('(Plin)')) {
        paymentMethod = 'Yape/Plin';
      } else if (transaction.description.includes('(Efectivo)')) {
        paymentMethod = 'Efectivo';
      }
      
      if (!paymentGroups[paymentMethod]) {
        paymentGroups[paymentMethod] = {
          id: `grouped-${paymentMethod.toLowerCase()}`,
          time: transaction.time, // Use the first transaction's time
          type: 'ingreso',
          description: `Ventas ${paymentMethod}`,
          amount: 0,
          shift: transaction.shift,
          shiftName: transaction.shiftName,
          count: 0
        };
      }
      
      paymentGroups[paymentMethod].amount += transaction.amount;
      paymentGroups[paymentMethod].count += 1;
    });
    
    // Actualizar descripción con el conteo
    Object.keys(paymentGroups).forEach(method => {
      const group = paymentGroups[method];
      group.description = `${group.count} Venta${group.count > 1 ? 's' : ''} ${method} (Total)`;
    });
    
    // Combinar transacciones agrupadas con otras transacciones
    const groupedSales = Object.values(paymentGroups);
    return [...otherTransactions, ...groupedSales].sort((a, b) => {
      // Mantener orden cronológico por hora
      const timeA = a.time.split(':').map(Number);
      const timeB = b.time.split(':').map(Number);
      return (timeA[0] * 60 + timeA[1]) - (timeB[0] * 60 + timeB[1]);
    });
  };



  const loadReportData = async () => {
    if (!filters.locationId || !filters.dateFrom) {
      return;
    }

    setReportData(prev => ({ ...prev, loading: true, error: null }));

    try {
      const data = await reportsService.getCashRegisterReportData(
        filters.locationId,
        filters.dateFrom,
        filters.dateTo || filters.dateFrom
      );

      console.log('📊 Report data received:', data);

      setReportData(prev => ({
        ...prev,
        loading: false,
        transactions: data.transactions || [],
        cashCount: data.cashCount,
        summary: data.summary,
        isOpened: data.isOpened,
      }));

      if (data.transactions.length === 0) {
        toast.info('No se encontraron registros de caja para esta fecha');
      }

    } catch (error: any) {
      console.error('❌ Error loading report data:', error);
      setReportData(prev => ({
        ...prev,
        loading: false,
        error: error.message || 'Error al cargar los datos del reporte',
      }));
      toast.error('Error al cargar el reporte de caja');
    }
  };

  // Sync date range with filters
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      dateFrom: dateFrom,
      dateTo: dateTo
    }));
  }, [dateFrom, dateTo]);

  const handleDateChange = (key: string, value: string) => {
    if (key === 'dateFrom') {
      setManualDates(value, dateTo);
      updateFilter('dateFrom', value);
    } else if (key === 'dateTo') {
      setManualDates(dateFrom, value);
      updateFilter('dateTo', value);
    }
  };



  const getGroupedTransactionsByCashRegister = () => {
    const transactions = filters.shift !== 'all' 
      ? reportData.transactions.filter(t => t.shift === filters.shift)
      : reportData.transactions;
      
    // Group by cash register sessions (identified by opening/closing pairs)
    const cashRegisterSessions: any[] = [];
    const otherTransactions: any[] = [];
    
    // Separate opening/closing balances from regular transactions
    const openingBalances = transactions.filter(t => String(t.id).includes('opening-balance'));
    const closingBalances = transactions.filter(t => String(t.id).includes('closing-balance'));
    const regularTransactions = transactions.filter(t => 
      !String(t.id).includes('opening-balance') && 
      !String(t.id).includes('closing-balance') &&
      !String(t.id).includes('difference-')
    );
    const differences = transactions.filter(t => String(t.id).includes('difference-'));
    
    // Group transactions by cash register session
    openingBalances.forEach((opening, index) => {
      const sessionId = opening.id.replace('opening-balance-', '');
      const closing = closingBalances.find(c => c.id === `closing-balance-${sessionId}`);
      const sessionDifference = differences.find(d => d.id === `difference-${sessionId}`);
      
      // Find transactions that belong to this session (between opening and closing times)
      const openTime = opening.time;
      const closeTime = closing?.time || '23:59';
      
      const sessionTransactions = regularTransactions.filter(t => {
        // Filter by sessionId if available (more precise)
        if (t.sessionId && sessionId) {
          return t.sessionId === sessionId;
        }
        // Fallback to time-based filtering
        return t.shift === opening.shift && 
               t.time >= openTime && 
               t.time <= closeTime;
      });
      
      const sessionNumber = index + 1;
      const sessionGroup = {
        sessionId,
        sessionNumber,
        shiftId: opening.shift,
        shiftName: opening.shiftName,
        openingTransaction: opening,
        closingTransaction: closing,
        differenceTransaction: sessionDifference,
        transactions: sessionTransactions,
        isOpen: !closing,
        subtotal: {
          ingresos: 0,
          egresos: 0,
          neto: 0,
          transactionCount: sessionTransactions.length + (closing ? 2 : 1) + (sessionDifference ? 1 : 0)
        }
      };
      
      // Calculate subtotals for this session (excluding closing balance from calculation)
      [opening, ...sessionTransactions, sessionDifference]
        .filter(t => t)
        .forEach(transaction => {
          if (transaction.amount > 0) {
            sessionGroup.subtotal.ingresos += transaction.amount;
          } else {
            sessionGroup.subtotal.egresos += Math.abs(transaction.amount);
          }
        });
      
      // For closed cash registers, use the closing balance as the final balance
      // For open cash registers, calculate based on transactions
      if (closing) {
        sessionGroup.subtotal.neto = closing.amount; // Use closing balance as final amount
      } else {
        sessionGroup.subtotal.neto = sessionGroup.subtotal.ingresos - sessionGroup.subtotal.egresos;
      }
      
      // Recalcular el conteo de transacciones considerando el agrupamiento
      const groupedSessionTransactions = groupTransactionsByPaymentMethod(sessionTransactions);
      sessionGroup.subtotal.transactionCount = groupedSessionTransactions.length + (closing ? 2 : 1) + (sessionDifference ? 1 : 0);
      
      cashRegisterSessions.push(sessionGroup);
    });
    
    // Handle any remaining transactions that don't belong to sessions
    if (openingBalances.length === 0) {
      // No sessions found, group by shift only
      const grouped = transactions.reduce((acc: any, transaction) => {
        const shiftKey = transaction.shift;
        if (!acc[shiftKey]) {
          acc[shiftKey] = {
            shiftId: shiftKey,
            shiftName: transaction.shiftName,
            transactions: [],
            subtotal: {
              ingresos: 0,
              egresos: 0,
              neto: 0,
              transactionCount: 0
            }
          };
        }
        
        acc[shiftKey].transactions.push(transaction);
        
        if (transaction.amount > 0) {
          acc[shiftKey].subtotal.ingresos += transaction.amount;
        } else {
          acc[shiftKey].subtotal.egresos += Math.abs(transaction.amount);
        }
        
        acc[shiftKey].subtotal.neto = acc[shiftKey].subtotal.ingresos - acc[shiftKey].subtotal.egresos;
        
        return acc;
      }, {});

      // Update transaction counts for grouped shifts
      Object.keys(grouped).forEach(shiftKey => {
        const groupedShiftTransactions = groupTransactionsByPaymentMethod(grouped[shiftKey].transactions);
        grouped[shiftKey].subtotal.transactionCount = groupedShiftTransactions.length;
      });

      // Return traditional shift grouping as fallback
      const shiftOrder = ['morning', 'afternoon', 'night'];
      return shiftOrder
        .filter(shiftId => grouped[shiftId])
        .map(shiftId => grouped[shiftId]);
    }
    
    // Sort sessions by date then opening time
    return cashRegisterSessions.sort((a, b) => {
      const dateA = a.openingTransaction?.date || '';
      const dateB = b.openingTransaction?.date || '';
      if (dateA !== dateB) return dateA.localeCompare(dateB);
      const timeA = a.openingTransaction.time.split(':').map(Number);
      const timeB = b.openingTransaction.time.split(':').map(Number);
      return (timeA[0] * 60 + timeA[1]) - (timeB[0] * 60 + timeB[1]);
    });
  };

  const groupedTransactions = getGroupedTransactionsByCashRegister();

  const calculateCashCountTotals = () => {
    if (!reportData.cashCount) {
      return {
        billetes: [],
        monedas: [],
        totalBilletes: 0,
        totalMonedas: 0,
        totalArqueo: 0,
        cantidadBilletes: 0,
        cantidadMonedas: 0,
      };
    }

    // Transform bills from object to array format
    const billetes = Object.entries(reportData.cashCount.bills || {}).map(([denomination, quantity]) => ({
      id: `bill-${denomination}`,
      type: 'billete',
      denomination: parseFloat(denomination),
      quantity: quantity,
      total: parseFloat(denomination) * quantity,
    })).filter(item => item.quantity > 0);

    // Transform coins from object to array format  
    const monedas = Object.entries(reportData.cashCount.coins || {}).map(([denomination, quantity]) => ({
      id: `coin-${denomination}`,
      type: 'moneda', 
      denomination: parseFloat(denomination),
      quantity: quantity,
      total: parseFloat(denomination) * quantity,
    })).filter(item => item.quantity > 0);
    
    const totalBilletes = billetes.reduce((sum, item) => sum + item.total, 0);
    const totalMonedas = monedas.reduce((sum, item) => sum + item.total, 0);
    const totalArqueo = reportData.cashCount.totalCounted;
    
    return { 
      billetes, 
      monedas, 
      totalBilletes, 
      totalMonedas, 
      totalArqueo,
      cantidadBilletes: billetes.reduce((sum, item) => sum + item.quantity, 0),
      cantidadMonedas: monedas.reduce((sum, item) => sum + item.quantity, 0)
    };
  };

  const { billetes, monedas, totalBilletes, totalMonedas, totalArqueo, cantidadBilletes, cantidadMonedas } = calculateCashCountTotals();


  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Reporte de Caja Registradora</h2>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="py-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Local</Label>
              <Select
                value={filters.locationId}
                onValueChange={(value) => updateFilter('locationId', value)}
                disabled={locationsLoading}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue>
                    {locationsLoading ? 'Cargando...' : locationsWithAll.find(l => l.id === filters.locationId)?.nombre || 'Seleccionar'}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {locationsWithAll.map(location => (
                    <SelectItem key={location.id} value={location.id}>
                      <div className="flex items-center">
                        <MapPin className="w-4 h-4 mr-2" />
                        {location.nombre}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Rango</Label>
              <DateRangeQuickFilter
                value={quickRange}
                onChange={setQuickRange}
                reportType="cashRegister"
              />
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Desde</Label>
              <Input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => handleDateChange('dateFrom', e.target.value)}
                className="w-[140px]"
                disabled={quickRange !== 'custom'}
              />
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Hasta</Label>
              <Input
                type="date"
                value={filters.dateTo}
                onChange={(e) => handleDateChange('dateTo', e.target.value)}
                className="w-[140px]"
                disabled={quickRange !== 'custom'}
              />
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Turno</Label>
              <Select
                value={filters.shift}
                onValueChange={(value) => updateFilter('shift', value)}
              >
                <SelectTrigger className="w-[160px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {REPORT_SHIFTS.map(shift => (
                    <SelectItem key={shift.id} value={shift.id}>{shift.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button
              onClick={loadReportData}
              disabled={reportData.loading}
              className="bg-macartur-primary hover:bg-macartur-primary/90"
            >
              {reportData.loading ? 'Cargando...' : 'Generar Reporte'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Loading State */}
      {reportData.loading && (
        <Card>
          <CardContent className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin mr-2" />
            <span>Cargando datos del reporte...</span>
          </CardContent>
        </Card>
      )}

      {/* Error State */}
      {reportData.error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="flex items-center py-4">
            <AlertCircle className="w-5 h-5 mr-2 text-red-600" />
            <div>
              <p className="font-medium text-red-900">Error al cargar el reporte</p>
              <p className="text-sm text-red-700">{reportData.error}</p>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              className="ml-auto"
              onClick={loadReportData}
            >
              Reintentar
            </Button>
          </CardContent>
        </Card>
      )}

      {/* No Data State */}
      {!reportData.loading && !reportData.error && reportData.transactions.length === 0 && (
        <Card className="border-yellow-200 bg-yellow-50">
          <CardContent className="flex items-center py-4">
            <AlertCircle className="w-5 h-5 mr-2 text-yellow-600" />
            <div>
              <p className="font-medium text-yellow-900">No hay datos de caja</p>
              <p className="text-sm text-yellow-700">
                No se encontraron registros de caja para esta fecha y local seleccionado.
              </p>
            </div>
          </CardContent>
        </Card>
      )}



      {/* Movimientos y Arqueo de Caja - Con pestañas - Show if we have transactions */}
      {!reportData.loading && !reportData.error && reportData.transactions.length > 0 && (
        <Card>
        <CardHeader>
          <CardTitle>Detalle de Caja</CardTitle>
          <p className="text-sm text-gray-600 mt-1">
            Movimientos y arqueo de caja para {filters.locationId === 'all' ? 'todos los locales' : `"${locationsWithAll.find(l => l.id === filters.locationId)?.nombre}"`}
            {filters.dateFrom && filters.dateTo && ` desde ${filters.dateFrom} hasta ${filters.dateTo}`}
          </p>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="movimientos" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="movimientos" className="flex items-center">
                <Clock className="w-4 h-4 mr-2" />
                Movimientos del Día
              </TabsTrigger>
              <TabsTrigger value="arqueo" className="flex items-center">
                <Wallet className="w-4 h-4 mr-2" />
                Arqueo de Caja
              </TabsTrigger>
            </TabsList>

            {/* Pestaña Movimientos */}
            <TabsContent value="movimientos" className="mt-6">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Turno</TableHead>
                      <TableHead>Hora</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Descripción</TableHead>
                      <TableHead className="text-right">Monto</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {groupedTransactions.map((sessionGroup) => (
                      <>
                        {/* Header del grupo de sesión de caja */}
                        <TableRow key={`header-${sessionGroup.sessionId || sessionGroup.shiftId}`} className="bg-gray-50 border-b-2">
                          <TableCell colSpan={5} className="font-bold text-gray-800">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center">
                                <Clock className="w-5 h-5 mr-2 text-gray-600" />
                                {sessionGroup.openingTransaction?.date && (
                                  <Badge variant="outline" className="mr-2 text-xs font-normal">
                                    {new Date(sessionGroup.openingTransaction.date + 'T00:00:00').toLocaleDateString('es-PE', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' })}
                                  </Badge>
                                )}
                                {sessionGroup.sessionNumber ? (
                                  <span>Caja #{sessionGroup.sessionNumber} - Turno {sessionGroup.shiftName}</span>
                                ) : (
                                  <span>Turno {sessionGroup.shiftName}</span>
                                )}
                                {sessionGroup.isOpen && (
                                  <Badge className="ml-2 bg-green-100 text-green-700">Abierta</Badge>
                                )}
                              </div>
                              {sessionGroup.sessionNumber && (
                                <span className="text-sm text-gray-600">
                                  {sessionGroup.openingTransaction?.time} - {sessionGroup.closingTransaction?.time || 'Abierta'}
                                </span>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                        
                        {/* Apertura de caja */}
                        {sessionGroup.openingTransaction && (
                          <TableRow className="bg-blue-50 border border-blue-200">
                            <TableCell className="pl-8">
                              <span className="text-blue-500 text-sm">📂</span>
                            </TableCell>
                            <TableCell className="font-medium">
                              {sessionGroup.openingTransaction.time}
                            </TableCell>
                            <TableCell>
                              <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">
                                Apertura
                              </Badge>
                            </TableCell>
                            <TableCell className="font-semibold text-blue-800">
                              {sessionGroup.openingTransaction.description}
                            </TableCell>
                            <TableCell className="text-right font-bold text-blue-600">
                              S/ {sessionGroup.openingTransaction.amount.toFixed(2)}
                            </TableCell>
                          </TableRow>
                        )}
                        
                        {/* Filas de transacciones regulares */}
                        {groupTransactionsByPaymentMethod(sessionGroup.transactions || [])?.map((transaction) => {
                          const isPaymentSummary = String(transaction.id).includes('-summary') || 
                                                  transaction.description.includes('--- Resumen de Métodos de Pago ---');
                          const isSeparator = transaction.description.includes('--- Resumen de Métodos de Pago ---');
                          
                          return (
                            <TableRow 
                              key={`${sessionGroup.sessionId || sessionGroup.shiftId}-${transaction.id}`} 
                              className={
                                isPaymentSummary ? 'bg-yellow-50 border-l-4 border-yellow-400' : 'bg-white'
                              }
                            >
                              <TableCell className="pl-8">
                                <span className="text-gray-500 text-sm">
                                  {isSeparator ? '═' : '└'}
                                </span>
                              </TableCell>
                              <TableCell className={`font-medium ${
                                isSeparator ? 'text-yellow-700' : ''
                              }`}>
                                {isSeparator ? '---' : transaction.time}
                              </TableCell>
                              <TableCell>
                                <Badge 
                                  variant={transaction.type === 'ingreso' ? 'default' : 'destructive'}
                                  className={
                                    isPaymentSummary
                                      ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-100'
                                      : transaction.type === 'ingreso' 
                                        ? 'bg-green-100 text-green-800 hover:bg-green-100' 
                                        : ''
                                  }
                                >
                                  {isPaymentSummary
                                    ? (isSeparator ? 'Resumen' : 'Método')
                                    : transaction.type === 'ingreso' ? 'Ingreso' : 'Egreso'
                                  }
                                </Badge>
                              </TableCell>
                              <TableCell className={
                                isPaymentSummary ? 'font-medium text-yellow-800' : ''
                              }>
                                {transaction.description}
                              </TableCell>
                              <TableCell className={`text-right font-medium ${
                                isPaymentSummary
                                  ? (transaction.amount === 0 ? 'text-yellow-600' : 'text-yellow-700 font-semibold')
                                  : transaction.amount > 0 ? 'text-green-600' : 'text-red-600'
                              }`}>
                                {transaction.amount === 0 ? '---' : `S/ ${Math.abs(transaction.amount).toFixed(2)}`}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        
                        {/* Diferencia de caja */}
                        {sessionGroup.differenceTransaction && (
                          <TableRow className="bg-orange-50 border border-orange-200">
                            <TableCell className="pl-8">
                              <span className="text-orange-500 text-sm">⚠️</span>
                            </TableCell>
                            <TableCell className="font-medium">
                              {sessionGroup.differenceTransaction.time}
                            </TableCell>
                            <TableCell>
                              <Badge className={`${
                                sessionGroup.differenceTransaction.type === 'ingreso' 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-red-100 text-red-800'
                              } hover:bg-orange-100`}>
                                Diferencia
                              </Badge>
                            </TableCell>
                            <TableCell className="font-semibold text-orange-800">
                              {sessionGroup.differenceTransaction.description}
                            </TableCell>
                            <TableCell className={`text-right font-bold ${
                              sessionGroup.differenceTransaction.type === 'ingreso' ? 'text-green-600' : 'text-red-600'
                            }`}>
                              S/ {sessionGroup.differenceTransaction.amount.toFixed(2)}
                            </TableCell>
                          </TableRow>
                        )}
                        
                        {/* Cierre de caja */}
                        {sessionGroup.closingTransaction && (
                          <TableRow className="bg-purple-50 border border-purple-200">
                            <TableCell className="pl-8">
                              <span className="text-purple-500 text-sm">🔒</span>
                            </TableCell>
                            <TableCell className="font-medium">
                              {sessionGroup.closingTransaction.time}
                            </TableCell>
                            <TableCell>
                              <Badge className="bg-purple-100 text-purple-800 hover:bg-purple-100">
                                Cierre
                              </Badge>
                            </TableCell>
                            <TableCell className="font-semibold text-purple-800">
                              {sessionGroup.closingTransaction.description}
                            </TableCell>
                            <TableCell className="text-right font-bold text-purple-600">
                              S/ {sessionGroup.closingTransaction.amount.toFixed(2)}
                            </TableCell>
                          </TableRow>
                        )}
                        
                      </>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            {/* Pestaña Arqueo */}
            <TabsContent value="arqueo" className="mt-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Billetes */}
                <div>
                  <h4 className="font-semibold text-gray-800 mb-4 flex items-center">
                    <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                    Billetes ({cantidadBilletes} unidades)
                  </h4>
                  <div className="space-y-2">
                    {billetes.map((item) => (
                      <div key={`billete-${item.denomination}`} className="flex justify-between items-center p-3 bg-green-50 rounded-lg border border-green-200">
                        <div className="flex items-center">
                          <span className="font-medium text-green-800">S/ {item.denomination}</span>
                          <span className="ml-2 text-sm text-gray-600">x {item.quantity}</span>
                        </div>
                        <span className="font-bold text-green-700">S/ {item.total.toFixed(2)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between items-center p-3 bg-green-100 rounded-lg border-2 border-green-300 font-bold">
                      <span className="text-green-800">Subtotal Billetes</span>
                      <span className="text-green-800">S/ {totalBilletes.toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                {/* Monedas */}
                <div>
                  <h4 className="font-semibold text-gray-800 mb-4 flex items-center">
                    <span className="w-2 h-2 bg-yellow-500 rounded-full mr-2"></span>
                    Monedas ({cantidadMonedas} unidades)
                  </h4>
                  <div className="space-y-2">
                    {monedas.map((item) => (
                      <div key={`moneda-${item.denomination}`} className="flex justify-between items-center p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                        <div className="flex items-center">
                          <span className="font-medium text-yellow-800">S/ {item.denomination.toFixed(2)}</span>
                          <span className="ml-2 text-sm text-gray-600">x {item.quantity}</span>
                        </div>
                        <span className="font-bold text-yellow-700">S/ {item.total.toFixed(2)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between items-center p-3 bg-yellow-100 rounded-lg border-2 border-yellow-300 font-bold">
                      <span className="text-yellow-800">Subtotal Monedas</span>
                      <span className="text-yellow-800">S/ {totalMonedas.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Total del Arqueo */}
              <div className="mt-6 p-4 bg-blue-50 rounded-lg border-2 border-blue-300">
                <div className="flex justify-between items-center">
                  <div>
                    <h4 className="font-bold text-blue-800 text-lg">Total Arqueo de Caja</h4>
                    <p className="text-sm text-blue-600">
                      {cantidadBilletes + cantidadMonedas} unidades totales ({cantidadBilletes} billetes + {cantidadMonedas} monedas)
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-blue-900">S/ {totalArqueo.toFixed(2)}</div>
                    <div className="text-sm text-blue-700">
                      <div>Billetes: S/ {totalBilletes.toFixed(2)}</div>
                      <div>Monedas: S/ {totalMonedas.toFixed(2)}</div>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
      )}
    </div>
  );
};